import { useInfiniteQuery } from "@tanstack/react-query";
import { Instructor } from "@/types";

interface UseInstructorsWithFiltersParams {
  limit: number;
  expertType?: string;
  status?: string;
  searchTerm?: string;
}

interface FetchInstructorsResponse {
  instructors: Instructor[];
  pagination: {
    total: number;
    hasNext: boolean;
    currentPage: number;
  };
}

async function fetchInstructorsWithFilters({
  page,
  limit,
  expertType = "",
  status = "active,approved",
  searchTerm = "",
}: UseInstructorsWithFiltersParams & {
  page: number;
}): Promise<FetchInstructorsResponse> {
  const queryParams = new URLSearchParams({
    page: String(page),
    limit: String(limit),
    status,
  });

  if (expertType && expertType !== "") {
    queryParams.append("types", expertType);
  }

  if (searchTerm && searchTerm.trim() !== "") {
    queryParams.append("search", searchTerm);
  }

  console.log("🔍 Fetching instructors with URL:", `/api/instructors?${queryParams.toString()}`);

  const response = await fetch(`/api/instructors?${queryParams.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch instructors: ${response.status}`);
  }

  const data = await response.json();
  
  console.log("✅ Instructors fetched:", data.instructors?.length || 0);
  console.log("📊 Pagination:", { total: data.total, hasMore: data.hasMore, page: data.page });

  return {
    instructors: data.instructors || [],
    pagination: {
      total: data.total || 0,
      hasNext: data.hasMore || false,
      currentPage: page,
    },
  };
}

export function useInstructorsWithFilters(params: UseInstructorsWithFiltersParams) {
  return useInfiniteQuery<FetchInstructorsResponse, Error>({
    queryKey: ["instructors-infinite", params],
    queryFn: ({ pageParam = 1 }) =>
      fetchInstructorsWithFilters({ ...params, page: pageParam as number }),
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNext
        ? lastPage.pagination.currentPage + 1
        : undefined;
    },
    initialPageParam: 1,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}
